import React, { useMemo, useState } from "react";

const TIER_FROM_SCORE = (score) => {
  if (score >= 85) return { tier: 'S', color: '#10b981', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.45)' };
  if (score >= 75) return { tier: 'A', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.45)' };
  if (score >= 65) return { tier: 'B', color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)', border: 'rgba(139,92,246,0.45)' };
  if (score >= 55) return { tier: 'C', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.45)' };
  return { tier: 'D', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.45)' };
};

const POS_COLORS = { QB: '#ef4444', RB: '#3b82f6', WR: '#10b981', TE: '#f59e0b' };
const NUM_ROUNDS = 4;
const PICKS_PER_ROUND = 12;
const TOTAL_PICKS = NUM_ROUNDS * PICKS_PER_ROUND;

// Pick-range badge: 1-4 Early, 5-8 Mid, 9-12 Late (12-team league convention)
const getPickRange = (pickInRound) => {
  if (pickInRound <= 4) return { label: 'EARLY', color: '#10b981', bg: 'rgba(16,185,129,0.18)' };
  if (pickInRound <= 8) return { label: 'MID',   color: '#f59e0b', bg: 'rgba(245,158,11,0.18)' };
  return                     { label: 'LATE',  color: '#94a3b8', bg: 'rgba(148,163,184,0.18)' };
};

const compositeScore = (player) => {
  const momentum = player?.amp_momentum_score || 0;
  const production = player?.amp_production_score || 0;
  const analytics = player?.amp_analytics_score || 0;
  return (momentum * 0.55) + (production * 0.30) + (analytics * 0.15);
};

const ProjectionsView = ({
  formatAdjustedPlayers,
  mockDraftYear,
  setMockDraftYear,
  isPaid,
  isLoggedIn,
  isMobileView,
}) => {
  const [posFilter, setPosFilter] = useState('All');
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const playerSlug = (player) =>
    ((player.first_name || '') + '_' + (player.last_name || '')).toLowerCase().replace(/[^a-z_]/g, '');

  const projectedPicks = useMemo(() => {
    return formatAdjustedPlayers
      .filter((p) => p.nfl_draft_year === mockDraftYear && p.rank != null)
      .filter((p) => p.player_type !== 'nfl')
      .sort((a, b) => a.rank - b.rank)
      .slice(0, TOTAL_PICKS);
  }, [formatAdjustedPlayers, mockDraftYear]);

  const tierCounts = useMemo(() => {
    const counts = { S: 0, A: 0, B: 0, C: 0, D: 0 };
    projectedPicks.forEach((p) => {
      const t = TIER_FROM_SCORE(compositeScore(p)).tier;
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [projectedPicks]);

  const rounds = useMemo(() => {
    const arr = [];
    for (let r = 0; r < NUM_ROUNDS; r++) {
      arr.push(projectedPicks.slice(r * PICKS_PER_ROUND, (r + 1) * PICKS_PER_ROUND));
    }
    return arr;
  }, [projectedPicks]);

  const yearLocked = (yr) => yr > 2026 && isPaid !== 1;

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: 16, padding: isMobileView ? 16 : 24 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: "2rem", fontWeight: 800, color: "#FFD700", marginBottom: 6 }}>
            Projections &amp; Values
          </h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem", marginBottom: 14 }}>
            The projected {mockDraftYear} rookie draft if every team drafts by Fantasy Amp's Superflex rankings.
          </p>

          {/* Year toggle (matches tier builder pattern) */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            {[2026, 2027, 2028].map((yr) => {
              const locked = yearLocked(yr);
              const active = mockDraftYear === yr;
              return (
                <button
                  key={yr}
                  onClick={() => { if (!locked) setMockDraftYear(yr); }}
                  style={{
                    padding: "6px 18px",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    borderRadius: 8,
                    border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.2)",
                    background: active ? "rgba(255,215,0,0.2)" : "rgba(255,255,255,0.05)",
                    color: locked ? "rgba(255,255,255,0.25)" : active ? "#FFD700" : "rgba(255,255,255,0.7)",
                    cursor: locked ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {yr}{locked ? " 🔒" : ""}
                </button>
              );
            })}
          </div>

          {/* Tier distribution summary */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            {['S', 'A', 'B', 'C', 'D'].map((t) => {
              const meta = TIER_FROM_SCORE(t === 'S' ? 90 : t === 'A' ? 80 : t === 'B' ? 70 : t === 'C' ? 60 : 50);
              return (
                <div key={t} style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: meta.bg,
                  border: `1px solid ${meta.border}`,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: meta.color,
                }}>
                  {t}: {tierCounts[t] || 0}
                </div>
              );
            })}
          </div>

          {/* Position filter */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
            {['All', 'QB', 'RB', 'WR', 'TE'].map((pos) => {
              const active = posFilter === pos;
              return (
                <button
                  key={pos}
                  onClick={() => setPosFilter(pos)}
                  style={{
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: active ? 700 : 500,
                    borderRadius: 6,
                    border: active ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.15)",
                    background: active ? "rgba(255,215,0,0.2)" : "transparent",
                    color: active ? "#FFD700" : "rgba(255,255,255,0.55)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {pos}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rounds */}
        {rounds.map((picks, rIdx) => {
          const roundNum = rIdx + 1;
          const rangeGroups = [
            { key: 'early', label: 'EARLY', picks: picks.slice(0, 4), startPickInRound: 1 },
            { key: 'mid',   label: 'MID',   picks: picks.slice(4, 8), startPickInRound: 5 },
            { key: 'late',  label: 'LATE',  picks: picks.slice(8, 12), startPickInRound: 9 },
          ];
          return (
            <div key={roundNum} style={{ marginBottom: 18 }}>
              <div style={{
                fontSize: "0.95rem",
                fontWeight: 800,
                color: "rgba(255,255,255,0.65)",
                letterSpacing: "0.5px",
                padding: "8px 4px",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                marginBottom: 8,
              }}>
                ROUND {roundNum}
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobileView ? "1fr" : "repeat(3, minmax(0, 1fr))",
                gap: 10,
              }}>
                {rangeGroups.map((rg) => {
                  const expandKey = `${roundNum}-${rg.key}`;
                  const isExpanded = expanded.has(expandKey);
                  const meta = getPickRange(rg.startPickInRound);
                  const matchingCount = posFilter === 'All'
                    ? rg.picks.length
                    : rg.picks.filter((p) => (p.position || p.metrics?.position) === posFilter).length;
                  const endPickInRound = rg.startPickInRound + 3;
                  return (
                    <div key={rg.key} style={{
                      borderRadius: 10,
                      border: `1px solid ${meta.color}40`,
                      background: meta.bg,
                      overflow: "hidden",
                      transition: "all 0.2s ease",
                    }}>
                      {/* Range header (click to toggle) */}
                      <button
                        onClick={() => toggleExpand(expandKey)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 14px",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#fff",
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                          fontFamily: "inherit",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                          <span style={{
                            fontSize: "0.9rem",
                            fontWeight: 800,
                            color: meta.color,
                            letterSpacing: "0.5px",
                          }}>{rg.label}</span>
                          <span style={{
                            fontSize: "0.7rem",
                            color: "rgba(255,255,255,0.5)",
                            fontWeight: 600,
                          }}>
                            {roundNum}.{String(rg.startPickInRound).padStart(2, '0')} – {roundNum}.{String(endPickInRound).padStart(2, '0')}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            {rg.picks.map((p, i) => {
                              const t = TIER_FROM_SCORE(compositeScore(p));
                              const name = `${p.first_name || ''} ${p.last_name || ''}`.trim();
                              return (p.headshot_url || p.image) ? (
                                <img
                                  key={i}
                                  src={p.headshot_url || p.image}
                                  alt={name}
                                  title={name}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: `2px solid ${t.color}`,
                                    background: "rgba(0,0,0,0.2)",
                                  }}
                                />
                              ) : (
                                <div key={i} title={name} style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  background: "rgba(255,255,255,0.08)",
                                  border: `2px solid ${t.color}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.6rem",
                                  fontWeight: 700,
                                  color: "rgba(255,255,255,0.6)",
                                }}>
                                  {(p.first_name?.[0] || '')}{(p.last_name?.[0] || '')}
                                </div>
                              );
                            })}
                            {Array.from({ length: 4 - rg.picks.length }).map((_, i) => (
                              <div key={`et-${i}`} style={{
                                width: 32, height: 32, borderRadius: "50%",
                                border: "1px dashed rgba(255,255,255,0.15)",
                              }} />
                            ))}
                          </div>
                          <span style={{
                            color: "rgba(255,255,255,0.55)",
                            fontSize: "0.85rem",
                            display: "inline-block",
                            transition: "transform 0.2s ease",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          }}>▶</span>
                        </div>
                        {posFilter !== 'All' && (
                          <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)" }}>
                            {matchingCount} {posFilter} in range
                          </div>
                        )}
                      </button>

                      {/* Expanded player list */}
                      {isExpanded && (
                        <div style={{
                          borderTop: `1px solid ${meta.color}30`,
                          padding: 8,
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}>
                          {rg.picks.map((player, i) => {
                            const pickInRound = rg.startPickInRound + i;
                            const pickLabel = `${roundNum}.${String(pickInRound).padStart(2, '0')}`;
                            const pos = player.position || player.metrics?.position || '';
                            const posColor = POS_COLORS[pos] || '#6b7280';
                            const score = compositeScore(player);
                            const tier = TIER_FROM_SCORE(score);
                            const posMatch = posFilter === 'All' || pos === posFilter;
                            const profileUrl = `https://fantasyamp.com/player/${playerSlug(player)}`;
                            return (
                              <div key={player.id || i} style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "6px 8px",
                                background: "rgba(255,255,255,0.05)",
                                borderLeft: `3px solid ${tier.color}`,
                                borderRadius: 6,
                                opacity: posMatch ? 1 : 0.25,
                                transition: "opacity 0.2s ease",
                              }}>
                                <div style={{
                                  fontSize: "0.7rem",
                                  fontWeight: 800,
                                  color: "rgba(255,255,255,0.5)",
                                  minWidth: 32,
                                  flexShrink: 0,
                                  letterSpacing: "0.3px",
                                }}>{pickLabel}</div>

                                {(player.headshot_url || player.image) ? (
                                  <img
                                    src={player.headshot_url || player.image}
                                    alt=""
                                    style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                  />
                                ) : (
                                  <div style={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    background: "rgba(255,255,255,0.1)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: "0.6rem", color: "rgba(255,255,255,0.4)", flexShrink: 0,
                                  }}>
                                    {(player.first_name?.[0] || '')}{(player.last_name?.[0] || '')}
                                  </div>
                                )}

                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <a
                                    href={profileUrl}
                                    target="_blank"
                                    rel="noopener"
                                    style={{
                                      fontSize: "0.78rem",
                                      fontWeight: 700,
                                      color: "#fff",
                                      textDecoration: "none",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "block",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#FFD700'; e.currentTarget.style.textDecoration = 'underline'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.textDecoration = 'none'; }}
                                  >
                                    {player.first_name} {player.last_name}
                                  </a>
                                  <div style={{
                                    fontSize: "0.6rem",
                                    marginTop: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                    <span style={{ color: posColor, fontWeight: 700 }}>{pos}</span>
                                    {player.draft_pick_capital ? (
                                      <span style={{ color: "rgba(255,255,255,0.4)" }}> • {player.draft_pick_capital}</span>
                                    ) : null}
                                  </div>
                                </div>

                                <div style={{
                                  padding: "2px 7px",
                                  borderRadius: 5,
                                  background: tier.bg,
                                  border: `1px solid ${tier.border}`,
                                  color: tier.color,
                                  fontSize: "0.72rem",
                                  fontWeight: 800,
                                  flexShrink: 0,
                                }}>
                                  {tier.tier}
                                </div>
                              </div>
                            );
                          })}
                          {/* Empty placeholders if range has fewer than 4 picks */}
                          {Array.from({ length: 4 - rg.picks.length }).map((_, i) => {
                            const pickInRound = rg.startPickInRound + rg.picks.length + i;
                            return (
                              <div key={`empty-${i}`} style={{
                                padding: "6px 8px",
                                background: "rgba(255,255,255,0.02)",
                                border: "1px dashed rgba(255,255,255,0.08)",
                                borderRadius: 6,
                                color: "rgba(255,255,255,0.25)",
                                fontSize: "0.7rem",
                                fontStyle: "italic",
                              }}>
                                {roundNum}.{String(pickInRound).padStart(2, '0')} — empty
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {projectedPicks.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.4)" }}>
            No ranked players found for {mockDraftYear}.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectionsView;
